import { motion } from "framer-motion";
import {
    ArrowRight,
    Building2,
    Car,
    Coffee,
    Droplets,
    Plug,
    Snowflake,
    Sparkles,
    Wifi,
    Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { scrollToId } from "@/lib/scroll";
import api from "@/lib/api";
import AmbientGlow from "@/components/AmbientGlow";

const EASE = [0.22, 1, 0.36, 1];

const ICON_MAP = {
    wifi: Wifi,
    snowflake: Snowflake,
    zap: Zap,
    building: Building2,
    plug: Plug,
    coffee: Coffee,
    sparkles: Sparkles,
    car: Car,
    droplets: Droplets,
};

const FEATURED_SPANS = ["lg:col-span-5", "lg:col-span-4", "lg:col-span-3"];

const FALLBACK_AMENITIES = [
    { id: "high-speed-internet", icon_key: "wifi", title: "High-Speed Internet", desc: "Fast, reliable connectivity for every seat, all day.", featured: true },
    { id: "air-conditioning", icon_key: "snowflake", title: "Air Conditioning", desc: "A comfortable, climate-controlled environment.", featured: true },
    { id: "power-backup", icon_key: "zap", title: "Power Backup", desc: "Uninterrupted work, even during outages.", featured: true },
    { id: "luxury-infrastructure", icon_key: "building", title: "Luxury Infrastructure", desc: "A thoughtfully designed, premium workspace environment.", featured: false },
    { id: "electricity", icon_key: "plug", title: "Electricity", desc: "Reliable power for an uninterrupted workday.", featured: false },
    { id: "tea-coffee", icon_key: "coffee", title: "Tea / Coffee", desc: "Fresh tea and coffee to keep you going.", featured: false },
    { id: "cleaning-staff", icon_key: "sparkles", title: "Cleaning Staff", desc: "A clean, well-maintained workspace every day.", featured: false },
    { id: "free-parking", icon_key: "car", title: "Free Parking", desc: "Secure parking on the premises at no extra cost.", featured: false },
    { id: "clean-washroom", icon_key: "droplets", title: "Clean Washroom", desc: "Hygienic, well-maintained washrooms.", featured: false },
];

const Crosshair = () => (
    <svg aria-hidden="true" className="absolute right-5 top-5 h-4 w-4 text-brand-blue/25" fill="none" viewBox="0 0 16 16">
        <path d="M8 0v16M0 8h16" stroke="currentColor" strokeWidth="1.2" />
    </svg>
);

const cardBase =
    "group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white transition-shadow duration-500 hover:shadow-[0_26px_55px_-30px_rgba(22,50,79,0.35)]";

const iconChip =
    "flex items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-brand-blue transition-all duration-500 group-hover:from-brand-purple group-hover:via-brand-blue group-hover:to-brand-teal group-hover:text-white";

export default function AmenitiesSection() {
    const [amenities, setAmenities] = useState(FALLBACK_AMENITIES);

    useEffect(() => {
        api.get("/api/amenities")
            .then((res) => {
                if (Array.isArray(res.data?.amenities) && res.data.amenities.length > 0) {
                    setAmenities(res.data.amenities);
                }
            })
            .catch(() => {});
    }, []);

    const featured = amenities.filter((a) => a.featured);
    const essentials = amenities.filter((a) => !a.featured);

    return (
        <section id="amenities" data-testid="amenities-section" className="cv-auto relative overflow-hidden py-20 sm:py-28">
            <div aria-hidden="true" className="blueprint-grid absolute inset-0 opacity-40" />
            <AmbientGlow color="#22B3A3" side="left" className="-left-40 bottom-0 h-[420px] w-[420px]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                {/* Intro */}
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <p
                            data-testid="amenities-eyebrow"
                            className="inline-flex items-center gap-2.5 rounded-full border border-brand-purple/15 bg-white/70 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-ink/60"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
                            Everything You Need
                        </p>
                        <h2 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                            Designed Around <span className="text-gradient">Your Workday.</span>
                        </h2>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="max-w-md text-sm font-medium leading-relaxed text-ink/55 md:text-base"
                    >
                        From high-speed connectivity to everyday essentials, Work Island gives you
                        the infrastructure to work comfortably and confidently.
                    </motion.p>
                </div>

                {/* Bento grid */}
                <div className="mt-14 grid grid-cols-2 gap-4 lg:grid-cols-12">
                    {featured.map((amenity, i) => {
                        const Icon = ICON_MAP[amenity.icon_key] || Sparkles;
                        return (
                        <motion.div
                            key={amenity.id}
                            initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0.4 }}
                            whileInView={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.75, ease: EASE, delay: i * 0.1 }}
                            whileHover={{ y: -4 }}
                            data-testid={`amenity-${amenity.id}`}
                            className={`${cardBase} col-span-2 p-7 sm:p-8 ${FEATURED_SPANS[i % FEATURED_SPANS.length]}`}
                        >
                            <Crosshair />
                            <span className={`${iconChip} h-12 w-12`}>
                                <Icon size={21} strokeWidth={2} className="transition-transform duration-500 group-hover:-translate-y-0.5" />
                            </span>
                            <h3 className="mt-6 text-xl font-bold tracking-tight text-ink">
                                {amenity.title}
                            </h3>
                            <p className="mt-1.5 max-w-[26ch] text-sm font-medium leading-relaxed text-ink/50">
                                {amenity.desc}
                            </p>
                            <span
                                aria-hidden="true"
                                className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-brand-purple via-brand-blue to-brand-teal transition-all duration-700 group-hover:w-full"
                            />
                        </motion.div>
                        );
                    })}

                    {essentials.map((amenity, i) => {
                        const Icon = ICON_MAP[amenity.icon_key] || Sparkles;
                        return (
                        <motion.div
                            key={amenity.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.55, ease: EASE, delay: 0.15 + i * 0.06 }}
                            whileHover={{ y: -3 }}
                            data-testid={`amenity-${amenity.id}`}
                            className={`${cardBase} col-span-1 flex items-start gap-3 p-5 sm:gap-4 sm:p-6 lg:col-span-4`}
                        >
                            <span className={`${iconChip} h-10 w-10 shrink-0`}>
                                <Icon size={17} strokeWidth={2.1} className="transition-transform duration-500 group-hover:-translate-y-0.5" />
                            </span>
                            <div className="min-w-0">
                                <h3 className="text-[14px] font-bold leading-snug tracking-tight text-ink sm:text-[15px]">
                                    {amenity.title}
                                </h3>
                                <p className="mt-1 text-[12px] font-medium leading-snug text-ink/45">
                                    {amenity.desc}
                                </p>
                            </div>
                        </motion.div>
                        );
                    })}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mt-16 flex flex-col items-center gap-5 text-center"
                >
                    <p className="text-lg font-bold tracking-tight text-ink md:text-xl">
                        See it for yourself.
                    </p>
                    <button
                        data-testid="amenities-book-demo-btn"
                        onClick={() => scrollToId("#book-demo")}
                        className="btn-gradient group inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-[15px] font-bold text-white"
                    >
                        Book a Free Demo
                        <ArrowRight size={17} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
